import { AlertTriangle, CheckCircle2, Info, ShieldAlert, X } from "lucide-react";
import { useToasts } from "@/hooks/useToasts";
import { cn } from "@/lib/cn";

const ICON = {
  info: Info,
  ok: CheckCircle2,
  warn: AlertTriangle,
  critical: ShieldAlert,
} as const;
const TONE = {
  info: "border-edge bg-surface-2 text-text",
  ok: "border-ok/40 bg-ok/10 text-ok",
  warn: "border-warn/40 bg-warn/10 text-warn",
  critical: "border-critical/40 bg-critical/10 text-critical",
};

export function Toaster() {
  const { toasts, dismiss } = useToasts();
  if (!toasts.length) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[100] flex flex-col items-center gap-2 px-4 sm:items-end sm:right-4 sm:left-auto">
      {toasts.map((t) => {
        const Icon = ICON[t.level];
        // G23: critical alarms and new-major/warn + operation-failure toasts
        // are safety events the operator must hear immediately — announce them
        // assertively. Routine info/ok confirmations stay polite (no interrupt).
        const urgent = t.level === "critical" || t.level === "warn";
        return (
          <div
            key={t.id}
            role={urgent ? "alert" : "status"}
            aria-live={urgent ? "assertive" : "polite"}
            aria-atomic={urgent || undefined}
            className={cn(
              "pointer-events-auto flex w-full max-w-sm animate-slide-in items-start gap-3 rounded-lg border bg-surface px-3.5 py-3 shadow-xl",
              TONE[t.level],
            )}
          >
            <Icon size={16} className="mt-0.5 shrink-0" />
            <div className="flex-1 leading-tight">
              <div className="text-sm font-semibold text-text">{t.title}</div>
              {t.detail && <div className="mt-0.5 text-[11px] text-text-muted">{t.detail}</div>}
              {t.action && (
                <button
                  onClick={() => { t.action?.onAction(); dismiss(t.id); }}
                  className="mt-2 rounded-md border border-current/30 bg-current/10 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider"
                >
                  {t.action.label}
                </button>
              )}
            </div>
            <button
              onClick={() => dismiss(t.id)}
              className="shrink-0 rounded p-1 text-text-faint transition hover:text-text"
              aria-label="Dismiss"
            >
              <X size={12} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
